import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { electronApis } from '@/features/electron-api';
import { getBundledMcpTemplate } from '@/features/integrations/bundled-mcp-templates';
import type {
  McpPermission,
  OnDeviceMcpCatalogEntry,
} from '@/features/integrations/integrations.types';
import {
  fullDiskAccessPermissionQueryOptions,
  screenRecordingPermissionQueryOptions,
  watchAccessibilityPermissionQueryOptions,
} from '@/query/permissions';

import type { ServerEntry } from '@littlebird/axon-mcp';

interface McpPermissionDefinition {
  label: string;
  /** What the settings banner's titles call it */
  accessName: string;
  /** What Littlebird uses it for, shown once it is granted */
  use: string;
  check: () => Promise<boolean>;
  /** Live status, polled while missing so a grant in System Settings shows up */
  useGranted: () => boolean | undefined;
  /** The macOS prompt where there is one, else the System Settings pane */
  requestAccess: () => Promise<unknown>;
  openSettings: () => Promise<unknown>;
  /**
   * Connecting waits until it is granted. Accessibility and Screen Recording
   * only stop the tool calls that need them, so connecting goes ahead and the
   * dialog and settings tab show their status instead.
   */
  blocksConnect: boolean;
}

export const MCP_PERMISSIONS: Record<McpPermission, McpPermissionDefinition> = {
  fullDiskAccess: {
    label: 'Full Disk Access',
    accessName: 'Full Disk Access',
    use: 'Used to read files macOS otherwise keeps private.',
    check: () => electronApis.system.checkFullDiskAccessPermission(),
    useGranted: () => useQuery(fullDiskAccessPermissionQueryOptions()).data,
    // macOS has no prompt for Full Disk Access
    requestAccess: () => electronApis.system.openFullDiskAccessSettings(),
    openSettings: () => electronApis.system.openFullDiskAccessSettings(),
    blocksConnect: true,
  },
  accessibility: {
    label: 'Accessibility',
    accessName: 'Accessibility',
    use: 'Used to read and act on what’s in your windows.',
    check: () => electronApis.context.checkAccessibilityPermissions(),
    useGranted: () => useQuery(watchAccessibilityPermissionQueryOptions()).data,
    requestAccess: () => electronApis.context.requestAccessibilityPermissions(),
    openSettings: () => electronApis.system.openAccessibilitySettings(),
    blocksConnect: false,
  },
  screenRecording: {
    label: 'Screen Recording',
    accessName: 'Screen Recording',
    use: 'Used to see what’s on your screen.',
    check: () => electronApis.context.checkScreenRecordingPermission(),
    useGranted: () => useQuery(screenRecordingPermissionQueryOptions()).data,
    requestAccess: () =>
      electronApis.context.requestScreenRecordingPermission(),
    // The Screen Recording pane, which also holds System Audio Recording
    openSettings: () => electronApis.system.openSystemAudioRecordingSettings(),
    blocksConnect: false,
  },
};

/**
 * Servers that hit a TCC denial during setup print an error mentioning Full
 * Disk Access (see packages/mcp-apple-mail). Matching on it covers entries
 * saved before templates declared `requiredPermissions`.
 */
const FDA_SETUP_FAILURE = /full disk access/i;

export function isFullDiskAccessSetupFailure(server: ServerEntry): boolean {
  return (
    server.lastSetup?.ok === false &&
    FDA_SETUP_FAILURE.test(server.lastSetup.detail ?? '')
  );
}

export function serverRequiredPermissions(
  server: ServerEntry,
  catalogEntries?: readonly OnDeviceMcpCatalogEntry[]
): McpPermission[] {
  const templateId = templateIdForServer(server);
  const declared = [
    ...(getBundledMcpTemplate(templateId)?.requiredPermissions ?? []),
    ...(catalogEntries?.find((entry) => entry.id === templateId)
      ?.required_permissions ?? []),
  ];
  if (isFullDiskAccessSetupFailure(server)) declared.push('fullDiskAccess');
  return [...new Set(declared)];
}

export async function findMissingPermissions(
  required: readonly McpPermission[]
): Promise<McpPermission[]> {
  const missing: McpPermission[] = [];
  for (const permission of required) {
    if (!(await MCP_PERMISSIONS[permission].check())) missing.push(permission);
  }
  return missing;
}

/** A failed connect reports the permissions it waits for; nothing else would surface the others */
export function permissionsShownBeforeConnect(
  required: readonly McpPermission[]
): McpPermission[] {
  return required.filter(
    (permission) => !MCP_PERMISSIONS[permission].blocksConnect
  );
}

export function permissionsBlockingConnect(
  required: readonly McpPermission[]
): McpPermission[] {
  return required.filter(
    (permission) => MCP_PERMISSIONS[permission].blocksConnect
  );
}

export function openFullDiskAccessSettings() {
  void electronApis.system.openFullDiskAccessSettings();
}

/**
 * Runs `onGranted` when the permission flips from denied to granted (the
 * `=== false` guard ignores the initial `undefined` query state).
 */
export function useOnFullDiskAccessGranted(
  granted: boolean | undefined,
  onGranted: () => void
): void {
  const previousGranted = useRef(granted);

  useEffect(() => {
    const flippedToGranted =
      previousGranted.current === false && granted === true;
    previousGranted.current = granted;
    if (flippedToGranted) onGranted();
  }, [granted, onGranted]);
}

/**
 * Re-runs setup for servers whose last run failed on Full Disk Access, as
 * soon as the user grants it (the permission query polls while missing).
 */
export function useRerunSetupWhenFullDiskAccessGranted(
  granted: boolean | undefined,
  servers: readonly ServerEntry[] | undefined,
  setupProgress: Record<string, string>
): void {
  useOnFullDiskAccessGranted(granted, () => {
    for (const server of servers ?? []) {
      if (
        server.setup &&
        isFullDiskAccessSetupFailure(server) &&
        !(server.slug in setupProgress)
      ) {
        void electronApis.axonMcp.runSetup(server.slug);
      }
    }
  });
}
